import { useState } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { PersonOutlined } from '@mui/icons-material';
import SettingCard from './SettingCard';
import SettingRow from './SettingRow';
import ProfilePicture from '@components/ProfilePicture';
import LanguagePicker from '@components/LanguagePicker';
import WolfButton from '@components/ui/WolfButton';
import WolfInput from '@components/ui/WolfInput';
import WolfDropdown, { DropdownSelectedItem } from '@components/ui/WolfDropdown';
import WolfTooltip from '@components/ui/WolfTooltip';
import { EditIcon24 } from '@components/ui/WolfIcons';
import { useTranslation } from '@hooks/useTranslation';
import { User } from '@models/user.model';

interface ProfileSectionProps {
  userData: User;
  reduxOrganizations: any[];
  onUpdateName: (firstName: string, lastName: string) => Promise<void>;
  onDefaultOrganizationChange: (selected: DropdownSelectedItem[]) => void;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({
  userData,
  reduxOrganizations,
  onUpdateName,
  onDefaultOrganizationChange,
}) => {
  const theme = useTheme();
  const localeSettings = useTranslation('settings');
  const localeCommon = useTranslation('common');

  const [isEditing, setIsEditing] = useState(false);
  const [firstName, setFirstName] = useState(userData.firstName || '');
  const [lastName, setLastName] = useState(userData.lastName || '');

  const handleEditClick = () => {
    setFirstName(userData.firstName || '');
    setLastName(userData.lastName || '');
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    await onUpdateName(firstName, lastName);
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    setFirstName(userData.firstName || '');
    setLastName(userData.lastName || '');
    setIsEditing(false);
  };

  return (
    <SettingCard
      title={localeSettings['profileInformation'] || localeSettings['profile']}
      icon={<PersonOutlined />}
      iconColor={theme.palette.primary.main}>
      <Box className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-4">
        <Box className="w-full sm:w-auto order-2 sm:order-1">
          {/* Name editing */}
          {isEditing ? (
            <Box className="flex flex-col sm:flex-row items-center gap-2">
              <WolfInput
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                label={localeCommon['firstName']}
                boxFullWidth={true}
              />
              <WolfInput
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                label={localeCommon['lastName']}
                className="mt-2 sm:mt-0 sm:ml-2"
                boxFullWidth={true}
              />
              <Box className="flex gap-2 mt-2 sm:mt-0 sm:ml-2">
                <WolfButton color="primary" onClick={handleSaveClick}>
                  {localeCommon['save']}
                </WolfButton>
                <WolfButton color="secondary" onClick={handleCancelClick}>
                  {localeCommon['cancel']}
                </WolfButton>
              </Box>
            </Box>
          ) : (
            <Box className="w-full flex flex-col sm:flex-row items-center text-center sm:text-left">
              <Typography variant="h4semibold" className="!mr-2">
                {userData.firstName + ' ' + userData.lastName}
              </Typography>
              <WolfTooltip title={localeSettings['editName']}>
                <span>
                  <IconButton className="mt-2 sm:mt-0 sm:ml-2 !p-1" onClick={handleEditClick}>
                    <EditIcon24 />
                  </IconButton>
                </span>
              </WolfTooltip>
            </Box>
          )}

          {/* Email */}
          <Box className="my-3 text-center sm:text-left">
            <Typography variant="h4" gutterBottom>
              {userData.email}
            </Typography>
          </Box>

          {/* Language Selection */}
          <SettingRow label={localeSettings['language'] + ':'}>
            <LanguagePicker />
          </SettingRow>

          {/* Default Organization Selection */}
          {reduxOrganizations && reduxOrganizations.length > 1 && (
            <SettingRow label={(localeSettings['defaultOrganization'] || 'Default Organization') + ':'}>
              <Box className="min-w-48">
                <WolfDropdown
                  label={
                    reduxOrganizations?.find((org) => org.organizationId === userData.defaultOrganizationId)?.name ||
                    'Select Organization'
                  }
                  items={
                    reduxOrganizations?.map((org) => ({
                      text: org.name,
                      value: org.organizationId,
                    })) || []
                  }
                  singleSelect={true}
                  initialSelectedItems={userData.defaultOrganizationId ? [userData.defaultOrganizationId] : []}
                  onChange={onDefaultOrganizationChange}
                />
              </Box>
            </SettingRow>
          )}
        </Box>

        {/* Profile Picture */}
        <Box className="order-1 sm:order-2">
          <ProfilePicture src={userData?.avatar || ''} name={userData.firstName} />
        </Box>
      </Box>
    </SettingCard>
  );
};

export default ProfileSection;
